import { MenuItem } from 'primeng/api';
import { Validators } from '@angular/forms';

/**
 * N.B.: CAMPI CONFIGURABILI NEI DATI DELLE RIGHE TABELLA:
 * removeEditable: boolean; --> rende "statica" una colonna editable se il flag viene settato a true rimuovendo di fatto il p-cellEditor
 * editDisabled: boolean; --> per disabilitare i campi "editable" della riga
 * actionTooltip: string; --> per settare tooltip di una action nei dati --> convenzione actionType + | + testo tooltip
 * groupBy: string; --> sottogruppi footer tabella
 */

// configurazione generale della tabella:
export interface DynTableConfig {

  clientManagedTable?: boolean; // IMPORTANTE!! fa si che non venga gestito il lazy loading, va tutto lato client

  searchFields?: Array<DynTableSearchFields>; // filtri di ricerca della tabella
  labels?: DynTableLabels; // labels "statiche" della tabella
  paginator?: boolean; // mostra o meno la paginazione della tabella
  sort?: string; // sorting tabella
  dir?: number; // direzione colonna ('asc', 'desc')
  listNotEditableFields?: Array<string>; //campi pilotati da editDisabled (se non definito saranno tutti)
  filterCollapsed?: boolean; // tendina filtri aperta/chiusa
  rowClickAction?: any; // riga cliccata
  multiSelectable?: boolean; // per rendere multi-selezionabili le righe della tabella
  singleSelectable?: boolean; // per rendere selezionabile una riga alla volta della tabella
  loading?: boolean; // mostra o meno il caricamento della tabella
  marginTable?: string; // margine tabella
  checkboxes?: boolean; // abilita la selezione delle righe con checkbox
  checkboxesHeader?: string; // mostra un header come titolo del campo checkbox
  checkboxesInfo?: string; // mostra un tooltip di info nell'header del campo checkbox
  chkboxSelectAllBtn?: boolean; // per poter utilizzare il pulsante di selezione multipla checkbox nell'header
  rowspanField?: string; // DynTableColumn name su cui eseguire il rowspan (serve sempre anche passare in input "rowSpanMetadata")

  // gestione filtri tabella:
  serverSideFilters?: boolean; // indica che si vuole usare i filtri lato "server" dentro la dropdown
  clientSideFilters?: boolean; // indica che si vuole usare i filtri lato "client" (abbiamo già tutti i dati) per colonna
  reordableRow?: boolean; // indica se una tabella ha le righe riordinabili(con d&d - drag and drop)
  editColsFocusDown?: boolean; // indica che si vuole che nelle celle editabili, il focus al TAB vada alla cella SOTTOSTANTE

  // PULSANTIERA SOTTO (summary):
  addRowTable?: boolean; // per mostrare il pulsante di aggiunta riga sotto la tabella
  addRowBtnTooltip?: string; // traduzione tooltip pulsante aggiunta riga
  showCustomBtn?: boolean; // per mostrare un pulsante collegato al "custom" output
  customBtnTooltip?: string; // traduzione tooltip pulsante custom action
  customBtnIconClass?: string; // classe font-awesome pulsante custom action

  colored?: boolean; // per colorare la riga
}

// label "statiche" della tabella:
export interface DynTableLabels {
  title?: string; // titolo della ricerca
  ricercaHeader?: string; // traduzione "Ricerca" header filtri
  filtraBtnLabel?: string; // eventuale traduzione "Filtra" button
  resetBtnLabel?: string; // eventuale traduzione "Reset" button
  totali: string;
  mostrati?: string;
  di?: string;
  azioniHeader?: string;
}

// configurazione di una colonna della tabella:
export interface DynTableColumn {
  name: string;
  label: string; // traduzione campo
  type?: DynTableColType;
  editType?: DynTableColEditType;
  editDecimals?: number; // numero di decimali se il campo è ha type = DynTableColEditType.NUMBER
  sortable?: boolean;
  
  searchable?: boolean; // indica che deve comparire la ricerca "classica" nell'header
  searchType?: DynTableSearchType; // tipo di ricerca
  searchTrue?: DynTableBadgeType; // tipo di badge == "true" (se ricerca BOOLEAN)
  searchFalse?: DynTableBadgeType; // tipo di badge == "false" (se ricerca BOOLEAN)
  searchMethod?: DynTableSearchMethod; // metodo di ricerca
  searchPh?: string; // placeholder visualizzato nel campo di ricerca
  searchOptions?: DynTableSearchOption[];

  style?: any;
  cellStyle?: any;
  sum?: boolean; // colonna sommabile
  editable?: boolean; // colonna editabile
  editOptions?: DynTableEditOption[];
  bgColor: string; // campo che contiene il colore di background della cella

  // CAMPI X EXPANDER:
  group?: string; // gruppo di appartenenza e chiave di traduzione titolo gruppo
  rows?: number; // numero di righe per pagina
  customCssClass?: any; // colonna con style e classe personalizzata
  grouped?: boolean; // per raggruppare e fare somme per sottogruppi
  valueExpander?: any; // valore del expander da recuperare(usato ad esempio suglio expander download)
  tooltip?: string; // messaggio tooltip da mostrare
}

// configurazione colonne unite con colspan:
export interface DynTableColSpan {
  name: string;
  label: string; // traduzione campo
  colspan: number;
}

export interface DynTableRowSpan {
  header: string; // traduzione header rowspan
  style: any; // traduzione campo
  // ...verrà aggiunta la config dei campi "custom" in base ai dati della tabella
}

// configurazione campi nei filtri di ricerca:
export interface DynTableSearchFields {
  name?: string; // nome campo
  label?: string; // traduzione campo
  type?: DynTableColFilterType; // tipo filtro (default input_contains ???)
  options?: any[]; // valori se type == "select"
  keyField?: string; // nome del campo chiave nel bean del filtro JPA (tipicamente "id")
  disabled?: boolean; // disabilitato true/false
  validators?: Array<Validators>; // validatori del campo
  defaultOption?: any; // valore default per il form select
  placeholder?: string; // placeholder per i campi di ricerca

  range?: boolean; // indica che è un field di tipo "range" da-a (2 formControl!)
  defaultValueFrom?: any; // valore default per range "da"
  defaultValueTo?: any; // valore default per range "a"
  nameFrom?: string; // nome campo range "da"
  nameTo?: string; // nome campo range "a"
}

// tipologia di filtro "server-side"
export class DynTableColFilterType {
  static INPUT_CONTAINS = 'input_contains';
  static EQUALS = 'equals';
  static SELECT = 'select';
  static DATE_SEARCH = 'dateSearch';
  static TIME_SEARCH = 'timeSearch';
  static DATE_RANGE = 'dateRange';
  static IN_VAL = 'inVal';
  static CALL_WS = 'callWs';
  static TIME_RANGE = 'time_range';
  static DATE_INTERVAL = 'date_interval';
  static TS_INTERVAL = 'ts_interval';
}

// configurazione campi filtri di ricerca "classici":
export interface DynTableColFiltersFields {
  name: string; // nome campo
  label: string; // traduzione campo
  type: string; // tipo filtro (default input_contains ???)
  options?: any[]; // valori se type == "select"
  disabled?: boolean; // disabilitato true/false
  validators?: Array<Validators>; // validatori del campo
  defaultOption?: any; // valore default per il form select
  placeholder?: string; // placeholder per i campi di ricerca
}

// configurazione della singola action della tabella:
export interface DynTableAction {
  type: string;
  icon: string;
  stackedIcons?: string[];
  disabled?: string;
  hidden?: string;
  tooltip?: string;
  tooltipLarge?: boolean;
  cls?: string;
  width?: string; // per larghezza colonna acttion
  splitButton?: boolean; // campo per attivare o meno il menu split-button
  items?: MenuItem[];
  onClick: (actionType: string, row: any) => void; // click su una delle azioni o expander
}

export interface IconActionData {
  actionType: string; // identificatore del tipo di icon-action
  row: any; // dati della riga dyn-table
}

// tipologie di colonne "speciali" (vedi metodo "renderField")
export class DynTableColType {
  static BOOLEAN = 'boolean';
  static IMAGE = 'image';
  static ICON = 'icon';
  static ICON_ACTION = 'icon_action'; // specificare con --> classeIcona;actionType
  static URL = 'url';
  static EURO = 'euro';
  static EURO_NO_DECIMAL = 'euroNoDecimal';
  static DATETIME = 'datetime';
  static CUSTOM = 'custom';
  static DATE = 'date';
  static MOMENT_DATE = 'momentDt';
  static MOMENT_TS = 'momentTs';
  static MOMENT_DATE_ARRAY = 'momentDateArray';
  static DOTS = 'dots';
  static BADGE = 'badge';
  static ROUTE = 'route';
  static PROGRESS = 'progress';
  static ICON_BADGE = 'icon_badge';
  static INFO_GROUP = 'info_group'; // visualizzazione di un set di pulsanti
  static CHECKABLE = 'checkable';
  static DOWNLOAD = 'download'; // per i download sugli expander
  static TOOLTIP = 'tooltip';
  static NUMBER2DECIMAL = 'number2decimal';
  static PERC_NUMBER2DECIMAL = 'perc_number2decimal';
  static NUMBER4DECIMAL = 'number4decimal';
  static SCENARIO_PREVISIONALE = 'scenario_previsionale';
  static UTILI_PERDITE = 'utiliPerdite';
}

// tipologie di input per colonne editabili
export class DynTableColEditType {
  static TEXT = 'TEXT';
  static NUMBER = 'NUMBER';
  static SELECT = 'SELECT';
  static BOOLEAN = 'BOOLEAN';
  static CALENDAR = 'CALENDAR'; // FIXME - NON FUNZIA BENISSIMO...(colpa di primeNG)
}

export class DynTableSearchType {
  static TEXT = 'text';
  static NUMBER = 'number';
  static BOOLEAN = 'boolean';
  static DATE = 'date';
  static TIME = 'time';
  static CURRENCY = 'currency';
  static SELECT = 'select';
  static MULTISELECT = 'multiselect';
}

export class DynTableBadgeType {
  static PRIMARY = 'badge badge-primary;';
  static INFO = 'badge badge-info;';
  static SUCCESS = 'badge badge-success;';
  static DANGER = 'badge badge-danger;';
}

export enum DynTableColWidth {
  MINI,
  VERY_SMALL,
  SMALL,
  MEDIUM,
  LARGE,
  VERY_LARGE,
  HUGE,
  MAX,
  OVERMAX,
  DATE_WIDTH,
  NDG_WIDTH,
  CUSTOM,
}

export enum DynTableColAlign {
  LEFT,
  RIGHT,
  CENTER,
}

export enum DynTableSearchMethod {
  startsWith, 
  contains, 
  endsWith, 
  equals, 
  notEquals, 
  in, 
  lt, 
  lte, 
  gt, 
  gte
}

export interface DynTableSearchOption {
  label?: string;
  value?: any;
}

export interface DynTableEditOption {
  label?: string;
  value?: any;
}

export interface DynTablePagination {
  orderField: string;
  orderDirection: string;
  currentPage: number;
  targetPage: number;
  totalPages: number;
  recordsPerPage: number;
  totalRecords: number;
}

export interface JPAPagination {
  page: number;
  size: number;
  totalRecords?: number;
  sort?: string[];
}

export interface CrudParams {
  criterion: Criterion;
  pagination: any;
  sorting: any;
}

export interface Criterion {
  type: string; // se ha nestedCriterions può essere "and", "or", ecc...
  name: string;
  params: any[];
  nestedCriterions: Criterion[];
}

export interface CheckedCell {
  index: number; // indice di riga
  value: any; // valore colonna checkable
  columnName: string; // nome colonna checkable
  rowData: any[]; // riga completa
}

